import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Stage,
  Layer,
  Line,
  Rect,
  Circle,
  Arrow,
  RegularPolygon,
  Shape,
  Transformer,
  Group,
  Text,
  Ellipse,
} from 'react-konva';
import BoardToolbar from './BoardToolbar';
import { Button, Input, message, Dropdown, Modal } from 'antd';
import './Board.css';
import {
  LinkOutlined,
  MoreOutlined,
  StarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DragOutlined,
  DeleteOutlined,
  ExportOutlined,
  StarFilled,
} from '@ant-design/icons';
import ShareItemPermision from '../share/ShareItemPermision';
import { Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import { useParams } from 'react-router-dom';
import MoveModal from '../Sidebar/MoveModal';
import DuplicateModal from '../Sidebar/DuplicateModal';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import SkeletonWithLoaders from 'components/common/Spinner';
import ConnectorLayer from './ConnectorLayer';
import ConnectedShapeOverlay from './ConnectedShapeOverlay';
import { getShapeCenter, getShapeEdgePoint } from './ConnectorLayer';
import VersionPanel from '../versionHistory/VersionPanel';
import { jsPDF } from 'jspdf';
import Cookies from 'js-cookie';

const SHAPE_COLORS = [
  '#e3f0ff',
  '#fff3cd',
  '#d4edda',
  '#f8d7da',
  '#e8d5f5',
  '#d1f0f0',
  '#ffe5cc',
  '#fce4ec',
];
const STROKE_COLORS = [
  '#3b82f6',
  '#f59e0b',
  '#10b981',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#ec4899',
];
let colorIdx = 0;
const nextColor = () => ({
  fill: SHAPE_COLORS[colorIdx % SHAPE_COLORS.length],
  stroke: STROKE_COLORS[colorIdx++ % STROKE_COLORS.length],
});

const DotGrid = ({ width, height, offsetX, offsetY, scale }) => {
  const ref = useRef();
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const spacing = 40 * scale;
    const r = Math.max(0.8, scale * 0.9);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#cbd5e1';
    const sx = ((offsetX % spacing) + spacing) % spacing;
    const sy = ((offsetY % spacing) + spacing) % spacing;
    for (let x = sx - spacing; x < width + spacing; x += spacing)
      for (let y = sy - spacing; y < height + spacing; y += spacing) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
  }, [width, height, offsetX, offsetY, scale]);

  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

const CanvasImage = ({ el, drag, tool }) => {
  const [img] = useImage(el.src);
  return (
    <Group {...drag} x={el.x} y={el.y}>
      <KonvaImage
        image={img}
        width={el.width || 200}
        height={el.height || 150}
        cornerRadius={4}
      />
    </Group>
  );
};

const Board = () => {
  const stageRef = useRef();
  const trRef = useRef();
  const containerRef = useRef();

  const [tool, setTool] = useState('select');
  const [lineStyle, setLineStyle] = useState({
    dash: [],
    tension: 0,
    straight: false,
  });
  const [shapeConfig, setShapeConfig] = useState({ type: 'rect' });
  const [strokeColor, setStrokeColor] = useState('#1a1a1a');
  const [penSize, setPenSize] = useState(2);
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [histIdx, setHistIdx] = useState(0);

  const [connectors, setConnectors] = useState([]);

  const [title, setTitle] = useState('');

  const [loading, setLoading] = useState(true);

  const [itemType, setItemType] = useState(null);

  const [isOpen, setIsOpen] = useState(false);

  const [isStarred, setIsStarred] = useState(false);

  const [moveOpen, setMoveOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [selectedDuplicateNode, setSelectedDuplicateNode] = useState(null);

  const [attachments, setAttachments] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [isVersionOpen, setIsVersionOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  const roleId = Number(Cookies.get('space-role-id'));

  const isAdmin = roleId === 1;

  const isViewer = roleId === 5;

  const elements = history[histIdx];

  const displayElements = previewContent
    ? previewContent.elements
    : history[histIdx];
  const displayConnectors = previewContent
    ? previewContent.connectors
    : connectors;

  const queryClient = useQueryClient();

  const { spaceid, boardid } = useParams();

  const navigate = useNavigate();

  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);

  const handleCloseVersion = () => {
    setIsVersionOpen(false);
    setPreviewContent(null);
  };

  const pushHistory = (newEls) => {
    const next = history.slice(0, histIdx + 1);
    next.push(newEls);
    setHistory(next);
    setHistIdx(next.length - 1);
  };

  const setElements = useCallback(
    (updater) => {
      const current = history[histIdx];
      const newEls = typeof updater === 'function' ? updater(current) : updater;
      pushHistory(newEls);
    },
    [history, histIdx],
  );

  const setElementsLive = useCallback(
    (updater) => {
      setHistory((prev) => {
        const copy = [...prev];
        const current = copy[histIdx];
        copy[histIdx] =
          typeof updater === 'function' ? updater(current) : updater;
        return copy;
      });
    },
    [histIdx],
  );

  const undo = () => {
    if (histIdx > 0) setHistIdx((i) => i - 1);
  };
  const redo = () => {
    if (histIdx < history.length - 1) setHistIdx((i) => i + 1);
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        undo();
      }
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === 'y' || (e.shiftKey && e.key === 'z'))
      ) {
        e.preventDefault();
        redo();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedId) {
          pushHistory(elements.filter((el) => el.id !== selectedId));
          setSelectedId(null);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [histIdx, history, selectedId, elements]);

  const [isDrawing, setIsDrawing] = useState(false);

  const [straightStart, setStraightStart] = useState(null);

  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const fn = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;
    if (selectedId) {
      const node = stageRef.current.findOne(`#el_${selectedId}`);
      if (node) {
        trRef.current.nodes([node]);
        trRef.current.getLayer()?.batchDraw();
        return;
      }
    }
    trRef.current.nodes([]);
    trRef.current.getLayer()?.batchDraw();
  }, [selectedId, elements]);

  const getPos = () => {
    const p = stageRef.current.getPointerPosition();
    return { x: (p.x - position.x) / scale, y: (p.y - position.y) / scale };
  };

  const updateElement = (id, changes) =>
    pushHistory(
      elements.map((el) => (el.id === id ? { ...el, ...changes } : el)),
    );

  const openTextEditor = (el) => {
    const stage = stageRef.current;
    const box = stage.container().getBoundingClientRect();
    const node = stage.findOne(`#el_${el.id}`);
    if (!node) return;
    const abs = node.getAbsolutePosition();
    const isCircle = el.type === 'circle';
    const w = isCircle ? el.radius * 2 : el.width || 180;
    const tOff = isCircle ? -el.radius : el.type === 'sticky' ? 28 : 0;
    const lOff = isCircle ? -el.radius : 0;

    const ta = document.createElement('textarea');
    ta.value = el.text || '';
    Object.assign(ta.style, {
      position: 'fixed',
      top: `${box.top + abs.y + tOff * scale}px`,
      left: `${box.left + abs.x + lOff * scale}px`,
      width: `${Math.max(w * scale, 80)}px`,
      minHeight: '30px',
      fontSize: `${14 * scale}px`,
      border: '2px solid #3b82f6',
      padding: '4px 8px',
      background: 'rgba(255,255,255,0.97)',
      outline: 'none',
      resize: 'none',
      borderRadius: '4px',
      zIndex: '9999',
      fontFamily: 'sans-serif',
      textAlign: 'center',
      boxShadow: '0 4px 12px rgba(59,130,246,0.25)',
      lineHeight: '1.5',
    });
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const finish = () => {
      updateElement(el.id, { text: ta.value });
      if (document.body.contains(ta)) document.body.removeChild(ta);
    };
    ta.addEventListener('blur', finish, { once: true });
    ta.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') ta.blur();
    });
  };

  const handleMouseDown = (e) => {
    if (previewContent) return;
    if (e.target === stageRef.current) setSelectedId(null);
    if (tool === 'select') return;

    const pos = getPos();

    if (tool === 'eraser') return;

    if (tool === 'stamp' && selectedStamp) {
      pushHistory([
        ...elements,
        {
          id: Date.now(),
          type: 'stamp',
          x: pos.x,
          y: pos.y,
          emoji: selectedStamp,
          fontSize: 36,
        },
      ]);
      setTool('select');
      setSelectedStamp(null);
      return;
    }

    setIsDrawing(true);

    if (tool === 'pen') {
      pushHistory([
        ...elements,
        {
          id: Date.now(),
          type: 'line',
          points: [pos.x, pos.y],
          stroke: strokeColor,
          strokeWidth: penSize,
          dash: [],
          tension: 0.3,
        },
      ]);
      return;
    }

    if (tool === 'line') {
      if (lineStyle.straight) {
        setStraightStart(pos);
        pushHistory([
          ...elements,
          {
            id: Date.now(),
            type: 'line',
            points: [pos.x, pos.y, pos.x, pos.y],
            stroke: strokeColor,
            strokeWidth: 2,
            dash: lineStyle.dash,
            tension: 0,
          },
        ]);
      } else {
        pushHistory([
          ...elements,
          {
            id: Date.now(),
            type: 'line',
            points: [pos.x, pos.y],
            stroke: strokeColor,
            strokeWidth: 2,
            dash: lineStyle.dash,
            tension: lineStyle.tension,
          },
        ]);
      }
      return;
    }

    if (tool === 'sticky') {
      pushHistory([
        ...elements,
        {
          id: Date.now(),
          type: 'sticky',
          x: pos.x,
          y: pos.y,
          width: 160,
          height: 140,
          fill: '#fff9c4',
          stroke: '#f0c040',
          text: 'Double-click to edit',
        },
      ]);
      setTool('select');
      return;
    }

    if (tool === 'textbox') {
      pushHistory([
        ...elements,
        {
          id: Date.now(),
          type: 'textbox',
          x: pos.x,
          y: pos.y,
          width: 180,
          height: 40,
          fill: 'transparent',
          stroke: '#1a1a1a',
          text: 'Text here',
        },
      ]);
      setTool('select');
      return;
    }

    if (tool === 'shape') {
      const colors = nextColor();
      pushHistory([
        ...elements,
        {
          id: Date.now(),
          type: shapeConfig.type,
          direction: shapeConfig.direction,
          double: shapeConfig.double,
          x: pos.x,
          y: pos.y,
          width: 120,
          height: 70,
          radius: 50,
          ...colors,
          text: '',
        },
      ]);
      setTool('select');
      return;
    }
  };

  const handleMouseMove = () => {
    if (!isDrawing) return;
    if (previewContent) return;
    const pt = getPos();

    if (tool === 'pen' || (tool === 'line' && !lineStyle.straight)) {
      setElementsLive((prev) => {
        const arr = [...prev];
        const last = arr[arr.length - 1];
        if (last?.type === 'line') {
          arr[arr.length - 1] = {
            ...last,
            points: [...last.points, pt.x, pt.y],
          };
        }
        return arr;
      });
      return;
    }

    if (tool === 'line' && lineStyle.straight && straightStart) {
      setElementsLive((prev) => {
        const arr = [...prev];
        const last = arr[arr.length - 1];
        if (last?.type === 'line') {
          arr[arr.length - 1] = {
            ...last,
            points: [straightStart.x, straightStart.y, pt.x, pt.y],
          };
        }
        return arr;
      });
    }
  };

  const handleMouseUp = () => {
    if (previewContent) return;
    setIsDrawing(false);
    setStraightStart(null);
    if (tool === 'pen' || tool === 'line') {
    }
  };

  const handleElementClick = (id, e) => {
    if (tool === 'eraser') {
      e.cancelBubble = true;
      pushHistory(elements.filter((el) => el.id !== id));
      return;
    }
    if (tool === 'select') {
      e.cancelBubble = true;
      setSelectedId(id);
    }
  };

  const handleImageUpload = (file) => {
    const canvasX = (-position.x + size.width / 2) / scale - 150;
    const canvasY = (-position.y + size.height / 2) / scale - 100;

    handleAddAttachment(file, { x: canvasX, y: canvasY }, file.type);

    return false;
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    const ptr = stage.getPointerPosition();
    const f = e.evt.deltaY < 0 ? 1.08 : 1 / 1.08;
    const ns = Math.min(Math.max(scale * f, 0.15), 6);
    setPosition({
      x: ptr.x - ((ptr.x - position.x) / scale) * ns,
      y: ptr.y - ((ptr.y - position.y) / scale) * ns,
    });
    setScale(ns);
  };

  const makeDragProps = (el, transformOverride) => ({
    id: `el_${el.id}`,
    draggable: tool === 'select',
    onClick: (e) => handleElementClick(el.id, e),
    onDragStart: (e) => {
      e.cancelBubble = true;
    },
    onDragEnd: (e) => {
      e.cancelBubble = true;
      updateElement(el.id, { x: e.target.x(), y: e.target.y() });
    },
    onTransformEnd:
      transformOverride ||
      ((e) => {
        const node = e.target;
        const sx = node.scaleX(),
          sy = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);
        updateElement(el.id, {
          x: node.x(),
          y: node.y(),
          width: Math.max((el.width || 120) * sx, 30),
          height: Math.max((el.height || 70) * sy, 30),
          radius: el.radius
            ? Math.max(el.radius * ((sx + sy) / 2), 15)
            : undefined,
        });
      }),
  });

  const ColorBar = () => {
    if (!selectedId) return null;
    const el = elements.find((e) => e.id === selectedId);
    if (!el || el.type === 'line' || el.type === 'stamp') return null;
    const stage = stageRef.current;
    if (!stage) return null;
    const node = stage.findOne(`#el_${selectedId}`);
    if (!node) return null;
    const abs = node.getAbsolutePosition();
    const box = stage.container().getBoundingClientRect();

    return (
      <div
        style={{
          position: 'fixed',
          top: box.top + abs.y - 50,
          left: box.left + abs.x,
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          padding: '5px 10px',
          display: 'flex',
          gap: 5,
          alignItems: 'center',
          zIndex: 2000,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          userSelect: 'none',
          flexWrap: 'nowrap',
        }}
      >
        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
          FILL
        </span>
        {SHAPE_COLORS.map((c) => (
          <div
            key={c}
            onClick={() => updateElement(selectedId, { fill: c })}
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              background: c,
              cursor: 'pointer',
              flexShrink: 0,
              border: el.fill === c ? '2px solid #3b82f6' : '1px solid #d1d5db',
            }}
          />
        ))}
        <div
          style={{
            width: 1,
            height: 16,
            background: '#e2e8f0',
            margin: '0 2px',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>
          BORDER
        </span>
        {STROKE_COLORS.map((c) => (
          <div
            key={c}
            onClick={() => updateElement(selectedId, { stroke: c })}
            style={{
              width: 16,
              height: 16,
              borderRadius: 3,
              background: c,
              cursor: 'pointer',
              flexShrink: 0,
              border:
                el.stroke === c ? '2px solid #1a1a1a' : '1px solid #d1d5db',
            }}
          />
        ))}
        <div
          style={{
            width: 1,
            height: 16,
            background: '#e2e8f0',
            margin: '0 2px',
            flexShrink: 0,
          }}
        />
        <button
          onClick={() => {
            pushHistory(elements.filter((e) => e.id !== selectedId));
            setSelectedId(null);
          }}
          style={{
            fontSize: 13,
            color: '#ef4444',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0 2px',
          }}
        >
          ✕
        </button>
      </div>
    );
  };

  const AttachmentDeleteOverlay = () => {
    if (!selectedId) return null;

    const el = elements.find((e) => e.id === selectedId);

    if (!el || el.type !== 'image' || !el.attachmentId) return null;

    const stage = stageRef.current;
    if (!stage) return null;

    const node = stage.findOne(`#el_${selectedId}`);
    if (!node) return null;

    const abs = node.getAbsolutePosition();
    const box = stage.container().getBoundingClientRect();

    const elWidth = (el.width || 300) * scale;

    return (
      <div
        style={{
          position: 'fixed',
          top: box.top + abs.y - 14,
          left: box.left + abs.x + elWidth - 14,
          zIndex: 3000,
        }}
      >
        <button
          onClick={async (e) => {
            e.stopPropagation();
            await deleteAttachment(el.attachmentId);
            setSelectedId(null);
          }}
          title="Delete attachment"
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: '#ef4444',
            color: '#fff',
            border: '2px solid #fff',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
            padding: 0,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
    );
  };

  const renderElement = (el) => {
    const drag = makeDragProps(el);
    const isSel = selectedId === el.id;
    const sw = isSel ? 2 : 1.5;
    const dbl = () => openTextEditor(el);
    const eraserCursor = tool === 'eraser' ? { style: { cursor: 'cell' } } : {};

    switch (el.type) {
      case 'line':
        return (
          <Line
            key={el.id}
            id={`el_${el.id}`}
            points={el.points}
            stroke={el.stroke || '#1a1a1a'}
            strokeWidth={el.strokeWidth || 2}
            dash={el.dash}
            tension={el.tension}
            lineCap="round"
            lineJoin="round"
            onClick={(e) => handleElementClick(el.id, e)}
            {...eraserCursor}
          />
        );

      case 'stamp':
        return (
          <Text
            key={el.id}
            {...drag}
            x={el.x}
            y={el.y}
            text={el.emoji}
            fontSize={el.fontSize || 36}
            {...eraserCursor}
          />
        );

      case 'rect':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Rect
              width={el.width}
              height={el.height}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
              cornerRadius={6}
            />
            <Text
              text={el.text || ''}
              width={el.width}
              height={el.height}
              align="center"
              verticalAlign="middle"
              fontSize={14}
              fill="#1a1a1a"
              onDblClick={dbl}
            />
          </Group>
        );

      case 'circle':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Circle
              radius={el.radius}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
            />
            <Text
              text={el.text || ''}
              width={el.radius * 2}
              height={el.radius * 2}
              x={-el.radius}
              y={-el.radius}
              align="center"
              verticalAlign="middle"
              fontSize={14}
              fill="#1a1a1a"
              onDblClick={dbl}
            />
          </Group>
        );

      case 'triangle':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <RegularPolygon
              sides={3}
              radius={el.radius}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
            />
          </Group>
        );

      case 'diamond':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <RegularPolygon
              sides={4}
              radius={el.radius}
              rotation={45}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
            />
          </Group>
        );

      case 'parallelogram-right':
      case 'parallelogram-left': {
        const off = el.type === 'parallelogram-right' ? 20 : -20;
        const w = el.width || 120,
          h = el.height || 60;
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Shape
              sceneFunc={(ctx, shape) => {
                ctx.beginPath();
                ctx.moveTo(off > 0 ? off : 0, 0);
                ctx.lineTo(off > 0 ? w : w + off, 0);
                ctx.lineTo(off > 0 ? w - off : w, h);
                ctx.lineTo(off > 0 ? 0 : -off, h);
                ctx.closePath();
                ctx.fillStrokeShape(shape);
              }}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
            />
            <Text
              text={el.text || ''}
              width={w}
              height={h}
              align="center"
              verticalAlign="middle"
              fontSize={14}
              fill="#1a1a1a"
              onDblClick={dbl}
            />
          </Group>
        );
      }

      case 'heart': {
        const sx = el.scaleX || 1,
          sy = el.scaleY || 1;
        return (
          <Group
            key={el.id}
            {...makeDragProps(el, (e) => {
              const node = e.target;
              const nsx = node.scaleX(),
                nsy = node.scaleY();
              node.scaleX(1);
              node.scaleY(1);
              updateElement(el.id, {
                x: node.x(),
                y: node.y(),
                scaleX: sx * nsx,
                scaleY: sy * nsy,
              });
            })}
            x={el.x}
            y={el.y}
          >
            <Shape
              sceneFunc={(ctx, shape) => {
                ctx.save();
                ctx.scale(sx, sy);
                ctx.beginPath();
                ctx.moveTo(0, 0);
                ctx.bezierCurveTo(0, -30, -40, -30, -40, 0);
                ctx.bezierCurveTo(-40, 30, 0, 50, 0, 70);
                ctx.bezierCurveTo(0, 50, 40, 30, 40, 0);
                ctx.bezierCurveTo(40, -30, 0, -30, 0, 0);
                ctx.closePath();
                ctx.fillStrokeShape(shape);
                ctx.restore();
              }}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
              width={80 * sx}
              height={70 * sy}
            />
          </Group>
        );
      }

      case 'sticky':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Rect
              width={el.width || 160}
              height={el.height || 140}
              fill={el.fill || '#fff9c4'}
              stroke={el.stroke || '#f0c040'}
              strokeWidth={sw}
              cornerRadius={4}
              shadowColor="rgba(0,0,0,0.10)"
              shadowBlur={8}
              shadowOffsetY={3}
            />
            <Rect
              width={el.width || 160}
              height={22}
              fill={el.stroke || '#f0c040'}
              cornerRadius={[4, 4, 0, 0]}
            />
            <Text
              text={el.text || ''}
              x={8}
              y={30}
              width={(el.width || 160) - 16}
              height={(el.height || 140) - 38}
              fontSize={13}
              fill="#374151"
              wrap="word"
              onDblClick={dbl}
            />
          </Group>
        );

      case 'textbox':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Rect
              width={el.width || 180}
              height={Math.max(el.height || 40, 40)}
              fill="transparent"
              stroke={isSel ? '#3b82f6' : 'rgba(0,0,0,0)'}
              strokeWidth={1.5}
              dash={[6, 3]}
              cornerRadius={4}
            />
            <Text
              text={el.text || 'Text here'}
              width={el.width || 180}
              fontSize={16}
              fill={el.stroke || '#1a1a1a'}
              onDblClick={dbl}
            />
          </Group>
        );

      case 'oval':
        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Ellipse
              radiusX={el.radiusX || 60}
              radiusY={el.radiusY || 35}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
            />
            <Text
              text={el.text || ''}
              width={(el.radiusX || 60) * 2}
              height={(el.radiusY || 35) * 2}
              x={-(el.radiusX || 60)}
              y={-(el.radiusY || 35)}
              align="center"
              verticalAlign="middle"
              fontSize={14}
              fill="#1a1a1a"
              onDblClick={dbl}
            />
          </Group>
        );

      case 'arrow': {
        const w = el.width || 120;
        const h = el.height || 120;
        const dir = el.direction || 'right';

        let points;

        if (dir === 'left') {
          points = [w, 0, 0, 0];
        } else if (dir === 'right') {
          points = [0, 0, w, 0];
        } else if (dir === 'up') {
          points = [0, h, 0, 0];
        } else {
          points = [0, 0, 0, h];
        }

        return (
          <Group
            key={el.id}
            {...makeDragProps(el, (e) => {
              const node = e.target;
              const sx = node.scaleX();
              const sy = node.scaleY();

              node.scaleX(1);
              node.scaleY(1);

              updateElement(el.id, {
                x: node.x(),
                y: node.y(),
                width: Math.max(w * sx, 30),
                height: Math.max(h * sy, 30),
              });
            })}
            x={el.x}
            y={el.y}
          >
            <Arrow
              points={points}
              stroke={el.stroke || '#1a1a1a'}
              fill={el.stroke || '#1a1a1a'}
              strokeWidth={2}
              pointerLength={10}
              pointerWidth={8}
              pointerAtBeginning={el.double}
            />

            <Rect
              width={dir === 'up' || dir === 'down' ? 20 : w}
              height={dir === 'left' || dir === 'right' ? 20 : h}
              x={dir === 'left' ? 0 : -10}
              y={dir === 'up' ? 0 : -10}
              fill="transparent"
            />
          </Group>
        );
      }

      case 'preparation': {
        const w = el.width || 120;
        const h = el.height || 60;

        return (
          <Group key={el.id} {...drag} x={el.x} y={el.y}>
            <Shape
              sceneFunc={(ctx, shape) => {
                ctx.beginPath();
                ctx.moveTo(w * 0.2, 0);
                ctx.lineTo(w * 0.8, 0);
                ctx.lineTo(w, h / 2);
                ctx.lineTo(w * 0.8, h);
                ctx.lineTo(w * 0.2, h);
                ctx.lineTo(0, h / 2);
                ctx.closePath();
                ctx.fillStrokeShape(shape);
              }}
              fill={el.fill}
              stroke={el.stroke}
              strokeWidth={sw}
            />
            <Text
              text={el.text || ''}
              width={w}
              height={h}
              align="center"
              verticalAlign="middle"
              fontSize={14}
              fill="#1a1a1a"
              onDblClick={dbl}
            />
          </Group>
        );
      }

      case 'image':
        return (
          <CanvasImage
            key={el.id}
            el={el}
            drag={makeDragProps(el, (e) => {
              const node = e.target;
              const sx = node.scaleX(),
                sy = node.scaleY();
              node.scaleX(1);
              node.scaleY(1);
              updateElement(el.id, {
                x: node.x(),
                y: node.y(),
                width: Math.max((el.width || 200) * sx, 30),
                height: Math.max((el.height || 150) * sy, 30),
              });
            })}
            tool={tool}
          />
        );

      default:
        return null;
    }
  };

  const handleStampSelect = (emoji) => {
    setSelectedStamp(emoji);
    setTool('stamp');
  };

  const handleSpawn = (newEl, newConnector, newId) => {
    pushHistory([...elements, newEl]);
    setConnectors((prev) => [...prev, newConnector]);
    setSelectedId(newId);
  };

  const handleRemoveConnector = (connectorId) => {
    setConnectors((prev) => prev.filter((c) => c.id !== connectorId));
  };

  const postContent = async () => {
    try {
      const payload = {
        space_id: Number(spaceid),
        item_id: boardid,
        content: JSON.stringify({ elements, connectors }),
      };
      const response = await httpInjectorService.postWhiteboardContent(payload);
      if (response?.status === 'success') {
        toast.success('Content saved succesfully!', {
          autoClose: 1500,
          position: 'top-right',
        });
        await getAttachment();
      }
    } catch (error) {
      toast.error('Failed to save board', {
        autoClose: 1500,
        position: 'top-right',
      });
    }
  };

  const GetBoardContent = async () => {
    setLoading(true);
    try {
      const response = await httpInjectorService.getPageContent(
        spaceid,
        boardid,
      );
      const data = response?.data;

      setTitle(data.title || 'Untitled');
      setIsStarred(data.is_stared === 1);
      setItemType(data.item_type);

      if (data.content) {
        const parsed =
          typeof data.content === 'string'
            ? JSON.parse(data.content)
            : data.content;

        if (Array.isArray(parsed)) {
          setHistory([parsed]);
        } else if (parsed?.elements) {
          setHistory([parsed.elements]);
          setConnectors(parsed.connectors || []);
        } else {
          setHistory([[]]);
          setConnectors([]);
        }
      } else {
        setHistory([[]]);
        setConnectors([]);
      }

      setHistIdx(0);
    } catch (error) {
      console.error(error);
      setHistory([[]]);
      setConnectors([]);
      setHistIdx(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    GetBoardContent();
    getAttachment();
  }, [boardid, spaceid]);

  useEffect(() => {
    setHistory([[]]);
    setHistIdx(0);
    setConnectors([]);
    setSelectedId(null);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setTitle('');
    setIsStarred(false);
    setItemType(null);
  }, [boardid]);

  const handleTitleUpdate = async (newTitle) => {
    try {
      await httpInjectorService.renameContent({
        space_id: Number(spaceid),
        item_id: Number(boardid),
        title: newTitle,
        item_type: itemType,
      });

      queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMove = async ({ dragId, dropId }) => {
    await httpInjectorService.movePageOrFolders({
      space_id: Number(spaceid),
      item_id: Number(boardid),
      parent_id: dropId,
    });
    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleDuplicate = () => {
    setSelectedDuplicateNode({
      item_id: boardid,
      item_type: 'whiteboard',
    });
    setDuplicateOpen(true);
  };

  const handleDuplicateConfirm = async ({
    original_id,
    target_space_id,
    parent_id,
    prefix,
    is_child,
  }) => {
    await httpInjectorService.duplicateContent({
      space_id: Number(target_space_id),
      item_id: Number(boardid),
      parent_id: parent_id || null,
      startname: prefix,
      is_child: 'no',
    });

    queryClient.invalidateQueries(['spaceContent', Number(spaceid)]);
  };

  const handleStarContent = async () => {
    try {
      const payload = {
        space_id: Number(spaceid),
        item_id: Number(boardid),
        item_type: 'whiteboard',
        action: 'star',
      };
      const response = await httpInjectorService.starShortcut(payload);
      // console.log(response);
      if (response.status === 'success') {
        toast.success(response.message, {
          autoClose: 1000,
          position: 'top-right',
        });
        setIsStarred(!isStarred);
      }
    } catch (error) {
      toast.error(error.message, {
        autoClose: 1000,
        position: 'top-right',
      });
    }
  };

  const handleDelete = async () => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id: boardid,
      status_type: 'deleted',
      nested_item: 'yes',
      type: 'whiteboard',
    });
    if (response.status === 'success') {
      setDeleteModalOpen(false);
      toast.success('Item Deleted successfully');
      navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
    }

    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleArchive = async () => {
    const response = await httpInjectorService.deleteorArchivePgorFolder({
      space_id: Number(spaceid),
      item_id: boardid,
      status_type: 'archived',
      nested_item: 'yes',
      type: 'whiteboard',
    });
    if (response.status === 'success') {
      setDeleteModalOpen(false);
      toast.success('Item Archives successfully');
      navigate(`${window.location.origin}/admin/confluence/space/${spaceid}`);
    }

    queryClient.invalidateQueries(['spaceContent', spaceid]);
  };

  const handleAddAttachment = async (file, inlinePosition = null, mimeType) => {
    try {
      const formData = new FormData();
      formData.append('space_id', spaceid);
      formData.append('item_id', boardid);
      formData.append('file', file);
      formData.append(
        'inline_position',
        inlinePosition ? JSON.stringify(inlinePosition) : 'null',
      );
      formData.append('mime_type', mimeType || file.type || '');

      const res = await httpInjectorService.postSpaceAttachment(formData);

      if (res?.status === 'success') {
        toast.success('Attachment uploaded!', { autoClose: 1500 });

        if (file.type?.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            pushHistory([
              ...elements,
              {
                id: Date.now(),
                type: 'image',
                x:
                  inlinePosition?.x ??
                  (-position.x + size.width / 2) / scale - 150,
                y:
                  inlinePosition?.y ??
                  (-position.y + size.height / 2) / scale - 100,
                width: 300,
                height: 200,
                src: ev.target.result,
                attachmentId: res?.data?.id ?? null,
              },
            ]);
          };
          reader.readAsDataURL(file);
        }
        await getAttachment();
      } else {
        toast.error('Upload failed.', { autoClose: 1500 });
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed.', { autoClose: 1500 });
    }
  };

  const getAttachment = async () => {
    try {
      const response = await httpInjectorService.getSpaceAttachments(
        spaceid,
        boardid,
      );
      const attachmentList = response?.data || [];
      setAttachments(attachmentList);
      return attachmentList;
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setAttachments([]);
      return [];
    }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      const res = await httpInjectorService.deleteSpaceAttachment(
        spaceid,
        boardid,
        attachmentId,
      );

      if (res?.status === 'success') {
        toast.success('Attachment deleted.', { autoClose: 1500 });

        setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));

        pushHistory(elements.filter((el) => el.attachmentId !== attachmentId));
      } else {
        toast.error('Delete failed.', { autoClose: 1500 });
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Delete failed.', { autoClose: 1500 });
    }
  };

  const handlePreviewVersion = async (versionId) => {
    try {
      const response = await httpInjectorService.getVersionDetails(
        spaceid,
        versionId,
      );

      if (response?.status !== 'success') {
        toast.error(response?.message || 'Failed to load version preview');
        return;
      }

      const versionData = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      const content = versionData?.content;

      if (!content) {
        toast.error('No content available for this version');
        return;
      }

      const parsed =
        typeof content === 'string' ? JSON.parse(content) : content;

      setPreviewContent({
        elements: parsed.elements || [],
        connectors: parsed.connectors || [],
      });
    } catch (err) {
      console.error('preview load error:', err);
      toast.error('Failed to load version preview');
    }
  };

  const handleExport = (format) => {
    const stage = stageRef.current;
    if (!stage) return;

    if (trRef.current) trRef.current.nodes([]);

    if (format === 'png') {
      const dataURL = stage.toDataURL({ pixelRatio: 2 });
      const a = document.createElement('a');
      a.href = dataURL;
      a.download = `${title || 'whiteboard'}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    if (format === 'pdf') {
      const dataURL = stage.toDataURL({ pixelRatio: 2 });

      import('jspdf')
        .then(({ jsPDF }) => {
          const pdf = new jsPDF({
            orientation:
              stage.width() > stage.height() ? 'landscape' : 'portrait',
            unit: 'px',
            format: [stage.width(), stage.height()],
          });

          pdf.addImage(dataURL, 'PNG', 0, 0, stage.width(), stage.height());
          pdf.save(`${title || 'whiteboard'}.pdf`);
        })
        .catch(() => {
          toast.error('Something went wrong');
        });
    }
  };

  const moreMenu = {
    items: [
      {
        key: 'star',
        icon: isStarred ? (
          <StarFilled style={{ color: '#faad14' }} />
        ) : (
          <StarOutlined />
        ),
        label: isStarred ? 'Unstar' : 'Star',
      },
      {
        key: 'export',
        icon: <ExportOutlined />,
        label: 'Export',
        children: [
          {
            key: 'export_png',
            label: 'Export as PNG',
            onClick: () => handleExport('png'),
          },
          {
            key: 'export_pdf',
            label: 'Export as PDF',
            onClick: () => handleExport('pdf'),
          },
        ],
      },
      {
        key: 'version',
        icon: <ClockCircleOutlined />,
        label: 'Version History',
        onClick: () => setIsVersionOpen((prev) => !prev),
      },
      {
        key: 'duplicate',
        icon: <CopyOutlined />,
        label: 'Duplicate',
      },
      {
        key: 'move',
        icon: <DragOutlined />,
        label: 'Move',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Archive and Delete',
      },
    ],
    onClick: ({ key }) => {
      if (key === 'export_png') handleExport('png');

      if (key === 'export_pdf') handleExport('pdf');

      if (key === 'star') handleStarContent();

      if (key === 'duplicate') handleDuplicate();

      if (key === 'move') setMoveOpen(true);

      if (key === 'delete') setDeleteModalOpen(true);
    },
  };

  if (loading) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SkeletonWithLoaders size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        {previewContent && (
          <div
            style={{
              position: 'fixed',
              top: 64,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: 8,
              padding: '6px 16px',
              fontSize: 13,
              zIndex: 3000,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <span>👁 Previewing version — canvas is read-only</span>
            <button
              onClick={() => setPreviewContent(null)}
              style={{
                background: '#f59e0b',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '2px 10px',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Back to current
            </button>
          </div>
        )}

        {/* Header */}
        <div className="editor-card-header">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={(e) => handleTitleUpdate(e.target.value)}
            bordered={false}
            className="editor-title-input"
            style={{ fontSize: 20, fontWeight: 600, width: 400 }}
          />
          <div className="editor-actions">
            {!isViewer && (
              <>
                <Button
                  style={{ backgroundColor: '#722ed1', color: 'white' }}
                  onClick={postContent}
                >
                  Save
                </Button>
                <Button icon={<LinkOutlined />} onClick={onOpen}>
                  Share
                </Button>
              </>
            )}
            {isAdmin && (
              <Dropdown menu={moreMenu} trigger={['click']}>
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </div>
        </div>

        <div className="editor-content-wrapper">
          <div className="editor-split-container">
            {/* Canvas Area */}
            <div
              ref={containerRef}
              style={{
                flex: 1,
                position: 'relative',
                overflowY: 'auto',
                overflowX: 'hidden',
                background: '#f5f5f5',
              }}
              className={`editor-left ${isVersionOpen ? 'shrink' : ''}`}
            >
              {!isVersionOpen && (
                <DotGrid
                  width={size.width}
                  height={size.height}
                  offsetX={position.x}
                  offsetY={position.y}
                  scale={scale}
                />
              )}

              {!isViewer && (
                <BoardToolbar
                  tool={tool}
                  setTool={(t) => {
                    setTool(t);
                    setSelectedId(null);
                  }}
                  lineStyle={lineStyle}
                  setLineStyle={setLineStyle}
                  shapeType={shapeConfig.type}
                  setShapeType={(type, direction, double) =>
                    setShapeConfig({ type, direction, double })
                  }
                  strokeColor={strokeColor}
                  setStrokeColor={setStrokeColor}
                  penSize={penSize}
                  setPenSize={setPenSize}
                  onUndo={undo}
                  onRedo={redo}
                  canUndo={histIdx > 0}
                  canRedo={histIdx < history.length - 1}
                  onStamp={handleStampSelect}
                  onImageUpload={handleImageUpload}
                  shapeConfig={shapeConfig}
                />
              )}

              {!previewContent && <ColorBar />}
              {!previewContent && <AttachmentDeleteOverlay />}

              {!previewContent && (
                <ConnectedShapeOverlay
                  selectedId={selectedId}
                  elements={elements}
                  tool={tool}
                  stageRef={stageRef}
                  scale={scale}
                  nextColor={nextColor}
                  onSpawn={handleSpawn}
                />
              )}

              {/* zoom */}
              <div
                style={{
                  position: 'fixed',
                  bottom: 24,
                  right: 24,
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowAttachments((v) => !v)}
                    title="Attachments"
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      border: showAttachments
                        ? '1.5px solid #6366f1'
                        : '1px solid #e2e8f0',
                      background: showAttachments ? '#ede9fe' : '#fff',
                      fontSize: 16,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    📎
                    {attachments.length > 0 && (
                      <span
                        style={{
                          position: 'absolute',
                          top: -5,
                          right: -5,
                          background: '#6366f1',
                          color: '#fff',
                          borderRadius: '50%',
                          width: 16,
                          height: 16,
                          fontSize: 9,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1.5px solid #fff',
                        }}
                      >
                        {attachments.length}
                      </span>
                    )}
                  </button>

                  {showAttachments && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 44,
                        right: 0,
                        width: 260,
                        maxHeight: '60vh',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.13)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        zIndex: 2000,
                      }}
                    >
                      <div
                        style={{
                          padding: '12px 14px 8px',
                          borderBottom: '1px solid #f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: '#fafafa',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1e293b',
                          }}
                        >
                          Attachments
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            background: '#6366f1',
                            color: '#fff',
                            borderRadius: 20,
                            padding: '1px 8px',
                            fontWeight: 600,
                          }}
                        >
                          {attachments.length}
                        </span>
                      </div>

                      <div
                        style={{ overflowY: 'auto', flex: 1, padding: '8px' }}
                      >
                        {attachments.length === 0 ? (
                          <div
                            style={{
                              textAlign: 'center',
                              color: '#94a3b8',
                              fontSize: 12,
                              padding: '20px 0',
                            }}
                          >
                            No attachments yet
                          </div>
                        ) : (
                          attachments.map((att) => {
                            const isImage = att.mime_type?.startsWith('image/');
                            const isPdf = att.mime_type === 'application/pdf';
                            const isWord = att.mime_type?.includes('word');
                            const icon = isImage
                              ? '🖼️'
                              : isPdf
                                ? '📄'
                                : isWord
                                  ? '📝'
                                  : '📎';

                            return (
                              <div
                                key={att.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  padding: '7px 8px',
                                  borderRadius: 8,
                                  marginBottom: 4,
                                  background: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.background = '#f0f4ff')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.background = '#f8fafc')
                                }
                              >
                                <div
                                  style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 6,
                                    background: isImage ? '#e0e7ff' : '#f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                  }}
                                >
                                  {isImage && att.attachment_url ? (
                                    <img
                                      src={att.attachment_url}
                                      alt={att.attachment_name}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 6,
                                      }}
                                    />
                                  ) : (
                                    <span style={{ fontSize: 16 }}>{icon}</span>
                                  )}
                                </div>

                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: '#1e293b',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={att.attachment_name}
                                  >
                                    {att.attachment_name}
                                  </div>
                                  {att.file_size && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: '#94a3b8',
                                        marginTop: 1,
                                      }}
                                    >
                                      {(att.file_size / 1024).toFixed(1)} KB
                                    </div>
                                  )}
                                </div>

                                <div style={{ display: 'flex', gap: 4 }}>
                                  {att.attachment_url && (
                                    <a
                                      href={att.attachment_url}
                                      download={att.attachment_name}
                                      target="_blank"
                                      rel="noreferrer"
                                      title="Download"
                                      style={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        background: '#e0f2fe',
                                        color: '#0284c7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 11,
                                        textDecoration: 'none',
                                      }}
                                    >
                                      ↓
                                    </a>
                                  )}
                                  <button
                                    onClick={() => deleteAttachment(att.id)}
                                    title="Delete"
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: '50%',
                                      background: '#fee2e2',
                                      color: '#ef4444',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: 11,
                                      fontWeight: 700,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: 0,
                                      flexShrink: 0,
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      <div
                        style={{
                          padding: '8px 10px',
                          borderTop: '1px solid #f1f5f9',
                          background: '#fafafa',
                        }}
                      >
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            padding: '6px',
                            borderRadius: 8,
                            border: '1.5px dashed #c7d2fe',
                            cursor: 'pointer',
                            fontSize: 12,
                            color: '#6366f1',
                            fontWeight: 600,
                            background: '#f5f3ff',
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = '#ede9fe')
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = '#f5f3ff')
                          }
                        >
                          <span>＋ Add Attachment</span>
                          <input
                            type="file"
                            hidden
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file)
                                handleAddAttachment(file, null, file.type);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                {[
                  { l: '+', f: () => setScale((s) => Math.min(s * 1.2, 6)) },
                  { l: '−', f: () => setScale((s) => Math.max(s / 1.2, 0.15)) },
                ].map(({ l, f }) => (
                  <button
                    key={l}
                    onClick={f}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      fontSize: 18,
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {l}
                  </button>
                ))}

                <div
                  style={{
                    textAlign: 'center',
                    fontSize: 11,
                    color: '#64748b',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    padding: '2px 4px',
                  }}
                >
                  {Math.round(scale * 100)}%
                </div>
              </div>

              {tool === 'stamp' && selectedStamp && (
                <div
                  style={{
                    position: 'fixed',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#1e293b',
                    color: '#fff',
                    borderRadius: 8,
                    padding: '6px 14px',
                    fontSize: 13,
                    zIndex: 2000,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                  }}
                >
                  Click anywhere to place {selectedStamp}
                </div>
              )}

              <Stage
                ref={stageRef}
                width={size.width}
                height={size.height}
                scaleX={scale}
                scaleY={scale}
                x={position.x}
                y={position.y}
                draggable={tool === 'select'}
                onDragEnd={(e) => {
                  if (e.target === stageRef.current)
                    setPosition({ x: e.target.x(), y: e.target.y() });
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onWheel={handleWheel}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  cursor:
                    tool === 'select'
                      ? 'default'
                      : tool === 'eraser'
                        ? 'cell'
                        : tool === 'stamp'
                          ? 'copy'
                          : tool === 'pen' || tool === 'line'
                            ? 'crosshair'
                            : 'copy',
                }}
              >
                <Layer>
                  {displayElements.map(renderElement)}
                  <Transformer
                    ref={trRef}
                    rotateEnabled={true}
                    borderStroke="#3b82f6"
                    borderStrokeWidth={1.5}
                    anchorStroke="#3b82f6"
                    anchorFill="#ffffff"
                    anchorSize={9}
                    anchorCornerRadius={3}
                    boundBoxFunc={(old, nw) =>
                      nw.width < 30 || nw.height < 30 ? old : nw
                    }
                  />
                </Layer>

                <ConnectorLayer
                  connectors={displayConnectors}
                  elements={displayElements}
                  onRemoveConnector={handleRemoveConnector}
                  tool={tool}
                />
              </Stage>
            </div>

            {isVersionOpen && (
              <div className="editor-right">
                <VersionPanel
                  typeid={boardid}
                  spaceid={spaceid}
                  onClose={handleCloseVersion}
                  GetBoardContent={GetBoardContent}
                  onPreview={handlePreviewVersion}
                  itemType={itemType}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ShareItemPermision
        isOpen={isOpen}
        onClose={onClose}
        space_id={spaceid}
        item_id={boardid}
      />

      <MoveModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        node={{ item_id: boardid }}
        spaceid={spaceid}
        onConfirm={handleMove}
      />

      <DuplicateModal
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        originalNode={selectedDuplicateNode}
        spaceid={spaceid}
        onConfirm={handleDuplicateConfirm}
      />

      <Modal
        open={deleteModalOpen}
        footer={null}
        onCancel={() => setDeleteModalOpen(false)}
      >
        <h3>Archive or Delete Page</h3>
        <p>
          Items in the trash can be restored or purged by a space admin. If you
          prefer, you can archive this instead.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            marginTop: '20px',
          }}
        >
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button onClick={handleArchive}>Archive</Button>
          <Button danger onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default Board;
